import React from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { Paths } from "../Services/EndPoints";
import { fetcher } from "../Utils/HelperFunctions";

// Exported so other store-chat entry points (e.g. the zero-results CTA) use the
// identical glyph and the identical number normalisation. Duplicating the
// 91-prefix rule would let one surface build a working wa.me link and another a
// broken one, with nothing failing loudly.
export function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]">
      <path d="M12 2a10 10 0 0 0-8.6 15l-1.4 5 5.1-1.3A10 10 0 1 0 12 2zm5.3 14.2c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7s-3.8-3.2-3.9-3.4c-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.3 0 .5l-.4.5c-.2.2-.3.4-.1.6.1.3.7 1.1 1.4 1.7.9.8 1.6 1 1.9 1.2.2.1.4.1.5-.1l.7-.9c.2-.2.3-.2.6-.1l1.9.9c.3.1.4.2.5.3.1.2.1.6-.1 1.1z" />
    </svg>
  );
}

// Normalise any stored number to a wa.me URL (prefix 91 for bare 10-digit
// Indian numbers).
export const waHref = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

/**
 * WhatsAppFAB
 *
 * Self-connected floating action button anchored bottom-right. Sources the
 * store owner's WhatsApp number + name from `about_me` itself (shared SWR
 * cache with StoreFooter — no extra request), so any page can just render
 * `<WhatsAppFAB />` and it always opens the store chat. No per-page wiring.
 *
 * @param {object}   props
 * @param {string}   [props.ownerFirstName] - Override the owner name label.
 * @param {string}   [props.phoneNumber]    - Override the WhatsApp number.
 * @param {string}   [props.replyTime]      - "~10 min" sub-label
 */
export default function WhatsAppFAB({
  ownerFirstName,
  phoneNumber,
  replyTime,
}) {
  const { t } = useTranslation();
  const { data: store } = useSWR(Paths.about_me, fetcher);

  const name = ownerFirstName || store?.firstName || "Meera";
  const href = waHref(phoneNumber || store?.mobileNumber);

  // The owner sets their contactable hours in the partner app. The BACKEND answers whether they
  // are contactable now, in IST -- not this browser, whose clock belongs to the buyer and may be
  // in any timezone. A shopper in London would otherwise see a Hyderabad seller as shut all
  // afternoon.
  //
  // Undefined (an older backend, or the request still in flight) is treated as available, so the
  // button never goes dark just because the answer has not arrived.
  const offline = store?.contactAvailable === false;

  const inner = (
    <>
      <span
        className={`w-[32px] h-[32px] rounded-full grid place-items-center flex-none ${
          offline ? "bg-ink-400 text-wc-paper" : "bg-whatsapp text-wc-paper"
        }`}
      >
        <WaIcon />
      </span>
      <span className="text-left">
        <span className="block text-[11px] font-[500] text-ink-900 leading-[1.2]">
          {t("waFab.ask", { name })}
        </span>
        <span className="block text-[9px] text-ink-400">
          {offline ? t("waFab.offline") : replyTime || t("waFab.replyTime")}
        </span>
      </span>
    </>
  );

  const shell = `
    fixed bottom-4 right-4 z-40 no-underline
    flex items-center gap-[9px]
    bg-wc-paper border border-wc-border rounded-pill
    py-[7px] pl-[8px] pr-[14px]
  `;

  // Rendered as a SPAN when offline, not a disabled anchor. An <a href> is followable by keyboard,
  // long-press and "open in new tab" no matter what styling says, so removing the element is the
  // only way the chat genuinely cannot be opened out of hours.
  if (offline) {
    return (
      <span
        aria-disabled="true"
        title={`${name} is offline right now`}
        className={`${shell} opacity-70 cursor-not-allowed`}
        style={{ boxShadow: "0 8px 22px rgba(31,33,46,.18)" }}
      >
        {inner}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Chat with ${name} on WhatsApp`}
      className={`${shell} active:bg-wc-gray transition-colors`}
      style={{ boxShadow: "0 8px 22px rgba(31,33,46,.18)" }}
    >
      {inner}
    </a>
  );
}
